import React, { useState } from 'react';
import { beginEnrollment, activateFactor } from '../../utils/mfaApi';
import TotpEnrollWidget from './TotpEnrollWidget';
import BackupCodesDisplay from './BackupCodesDisplay';

/**
 * MfaEnrollmentFlow
 *
 * State machine:
 *   idle → choosing → enrolling-<type> → showing-backup-codes → done
 *
 * Props:
 *   onComplete(): void   — parent refreshes the factor list
 *   onCancel(): void
 */
export default function MfaEnrollmentFlow({ onComplete, onCancel }) {
  const [stage, setStage] = useState('choosing');
  const [enrollment, setEnrollment] = useState(null);   // { factor_id, display }
  const [backupCodes, setBackupCodes] = useState(null); // string[]
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const startEnrollment = async (type) => {
    setError('');
    setLoading(true);
    try {
      const res = await beginEnrollment(type);
      setEnrollment(res);
      setStage(`enrolling-${type}`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not start enrollment');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpActivate = async (factorId, code) => {
    // Throws on failure — TotpEnrollWidget catches and shows the error
    await activateFactor(factorId, code);
    // Auto-enroll backup codes immediately after TOTP succeeds
    const bc = await beginEnrollment('backup_codes');
    setBackupCodes(bc.display.codes);
    setStage('showing-backup-codes');
  };

  if (stage === 'choosing') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <h4 className="text-sm font-semibold text-gray-900">Add a two-factor method</h4>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm" role="alert">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={() => startEnrollment('totp')}
          disabled={loading}
          className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <div className="text-left">
            <div className="text-sm font-medium text-gray-900">Authenticator app</div>
            <div className="text-xs text-gray-500">
              Google Authenticator, Authy, 1Password, etc.
            </div>
          </div>
          <span className="text-blue-600 text-sm">→</span>
        </button>
        {/* Future factor types render here. New entry = one button + one startEnrollment call. */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-gray-500 hover:underline"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'enrolling-totp' && enrollment) {
    return (
      <TotpEnrollWidget
        enrollment={enrollment}
        onActivate={handleTotpActivate}
        onCancel={() => setStage('choosing')}
      />
    );
  }

  if (stage === 'showing-backup-codes' && backupCodes) {
    return (
      <BackupCodesDisplay
        codes={backupCodes}
        onAcknowledge={onComplete}
      />
    );
  }

  return null;
}
